import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Divider,
  MenuItem,
  CircularProgress,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
} from "@mui/icons-material";
import { db, storage } from "../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Swal from "sweetalert2";

const PRIMARY = "#0d9488";

const getToday = () => new Date().toISOString().split("T")[0];

const getDefaultForm = () => ({
  vehicleId: "",
  vehicleNumber: "",
  driverId: "",
  driverName: "",
  vendorName: "",
  location: "",
  date: getToday(),
  amount: "",
  person: "",
  description: "",
  image: "",
});

function Vehicalmgmt() {
  const [records, setRecords] = useState([]);
  const [vehicleList, setVehicleList] = useState([]);
  const [driverList, setDriverList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [form, setForm] = useState(getDefaultForm());

  useEffect(() => {
    fetchVehicles();
    fetchDrivers();
    fetchRecords();
  }, []);

  const getVehicleNumber = (vehicle) =>
    vehicle?.vehicleNumber ||
    vehicle?.vehicle_no ||
    vehicle?.number ||
    vehicle?.registrationNumber ||
    vehicle?.name ||
    "Vehicle";

  const getDriverNameValue = (driver) =>
    driver?.name ||
    driver?.firstname ||
    driver?.fullName ||
    driver?.driverName ||
    "Driver";

  const getRecordVehicleNumber = (row) => {
    if (row.vehicleNumber) return row.vehicleNumber;

    return vehicleList.find((v) => v.id === row.vehicleId)?.number || "N/A";
  };

  const getRecordDriverName = (row) => {
    if (row.driverName) return row.driverName;

    return driverList.find((d) => d.id === row.driverId)?.name || "N/A";
  };

  const fetchVehicles = async () => {
    try {
      const snap = await getDocs(collection(db, "vehicles"));

      setVehicleList(
        snap.docs.map((item) => ({
          id: item.id,
          number: getVehicleNumber(item.data()),
          ...item.data(),
        }))
      );
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to load vehicles", "error");
    }
  };

  const fetchDrivers = async () => {
    try {
      const mergedDrivers = new Map();

      const driversSnap = await getDocs(collection(db, "drivers"));
      driversSnap.docs.forEach((item) => {
        mergedDrivers.set(item.id, {
          id: item.id,
          name: getDriverNameValue(item.data()),
          ...item.data(),
        });
      });

      const usersSnap = await getDocs(
        query(collection(db, "users"), where("role", "==", "Driver"))
      );

      usersSnap.docs.forEach((item) => {
        mergedDrivers.set(item.id, {
          id: item.id,
          name: getDriverNameValue(item.data()),
          ...item.data(),
        });
      });

      setDriverList(Array.from(mergedDrivers.values()));
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to load drivers", "error");
    }
  };

  const fetchRecords = async () => {
    setLoading(true);

    try {
      const q = query(collection(db, "vehicle_logs"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);

      setRecords(
        snap.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }))
      );
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to load records", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setUploading(true);

    try {
      const storageRef = ref(storage, `vehicle_logs/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);

      const url = await getDownloadURL(storageRef);

      setForm((prev) => ({
        ...prev,
        image: url,
      }));
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Image upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    if (!form.vehicleId) return "Please select vehicle";
    if (!form.driverId) return "Please select driver";
    if (!form.date) return "Please select date";
    if (!form.vendorName.trim()) return "Please enter vendor/shop name";
    if (!form.location.trim()) return "Please enter location";
    if (!form.person.trim()) return "Please enter person name";
    if (!form.amount) return "Please enter amount";
    if (Number(form.amount) <= 0) return "Amount must be greater than zero";
    if (!form.description.trim()) return "Please enter description";

    return "";
  };

  const handleSubmit = async () => {
    const validationMessage = validateForm();

    if (validationMessage) {
      Swal.fire("Error", validationMessage, "error");
      return;
    }

    setLoading(true);

    try {
      const selectedVehicle = vehicleList.find((v) => v.id === form.vehicleId);
      const selectedDriver = driverList.find((d) => d.id === form.driverId);

      const payload = {
        ...form,
        vehicleNumber: selectedVehicle?.number || form.vehicleNumber || "",
        driverName: selectedDriver?.name || form.driverName || "",
        vendorName: form.vendorName.trim(),
        location: form.location.trim(),
        person: form.person.trim(),
        description: form.description.trim(),
        amount: Number(form.amount),
        updatedAt: Timestamp.now(),
      };

      if (editId) {
        await updateDoc(doc(db, "vehicle_logs", editId), payload);
        Swal.fire("Updated", "Record updated successfully", "success");
      } else {
        await addDoc(collection(db, "vehicle_logs"), {
          ...payload,
          createdAt: Timestamp.now(),
        });

        Swal.fire("Saved", "Record added successfully", "success");
      }

      setOpen(false);
      resetForm();
      fetchRecords();
    } catch (error) {
      console.error(error);
      Swal.fire("Error", error.message || "Save failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Record?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });

    if (!confirm.isConfirmed) return;

    try {
      await deleteDoc(doc(db, "vehicle_logs", id));
      Swal.fire("Deleted", "Record deleted successfully", "success");
      fetchRecords();
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Delete failed", "error");
    }
  };

  const handleEdit = (row) => {
    setEditId(row.id);

    setForm({
      vehicleId: row.vehicleId || "",
      vehicleNumber: row.vehicleNumber || "",
      driverId: row.driverId || "",
      driverName: row.driverName || "",
      vendorName: row.vendorName || "",
      location: row.location || "",
      date: row.date || getToday(),
      amount: row.amount || "",
      person: row.person || "",
      description: row.description || "",
      image: row.image || "",
    });

    setOpen(true);
  };

  const resetForm = () => {
    setEditId(null);
    setForm(getDefaultForm());
  };

  const filteredData = useMemo(() => {
    const value = search.toLowerCase();

    return records.filter((row) => {
      const vehicle = getRecordVehicleNumber(row).toLowerCase();
      const driver = getRecordDriverName(row).toLowerCase();
      const person = String(row.person || "").toLowerCase();
      const description = String(row.description || "").toLowerCase();
      const vendor = String(row.vendorName || "").toLowerCase();
      const location = String(row.location || "").toLowerCase();

      return (
        vehicle.includes(value) ||
        driver.includes(value) ||
        person.includes(value) ||
        description.includes(value) ||
        vendor.includes(value) ||
        location.includes(value)
      );
    });
  }, [records, search, vehicleList, driverList]);

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box p={1} sx={{ background: "#f8fafc", minHeight: "100vh" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold">
          Vehicle Logs & Expenses
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ bgcolor: PRIMARY }}
          onClick={() => {
            resetForm();
            setOpen(true);
          }}
        >
          New Entry
        </Button>
      </Stack>

      <Paper sx={{ p: 1, borderRadius: 2 }} elevation={0}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by vehicle, driver, person, description or vendor..."
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "gray" }} />,
          }}
        />

        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f1f5f9" }}>
              <TableRow>
                <TableCell><b>Sr.</b></TableCell>
                <TableCell><b>Image</b></TableCell>
                <TableCell><b>Date / Vehicle</b></TableCell>
                <TableCell><b>Driver / Vendor</b></TableCell>
                <TableCell><b>Location</b></TableCell>
                <TableCell><b>Details</b></TableCell>
                <TableCell><b>Amount</b></TableCell>
                <TableCell align="center"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length ? (
                paginatedData.map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                    <TableCell>
                      <Avatar
                        src={row.image}
                        variant="rounded"
                        sx={{
                          width: 42,
                          height: 42,
                          cursor: row.image ? "pointer" : "default",
                        }}
                        onClick={() => {
                          if (row.image) {
                            setPreviewImage(row.image);
                            setPreviewOpen(true);
                          }
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      {row.date}
                      <br />
                      <Typography variant="caption" color="primary" fontWeight="bold">
                        {getRecordVehicleNumber(row)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {getRecordDriverName(row)}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {row.vendorName || "No Vendor"}
                      </Typography>
                    </TableCell>

                    <TableCell>{row.location || "-"}</TableCell>

                    <TableCell>
                      <Typography variant="body2">{row.description}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        By: {row.person}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <b>₹{row.amount}</b>
                    </TableCell>

                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleEdit(row)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>

                      <IconButton size="small" onClick={() => handleDelete(row.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          rowsPerPageOptions={[5, 10, 25, 50]}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editId ? "Edit Log" : "Add New Log"}</DialogTitle>
        <Divider />

        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Select Vehicle"
                fullWidth
                size="small"
                value={form.vehicleId}
                onChange={(event) => {
                  const selectedVehicle = vehicleList.find(
                    (v) => v.id === event.target.value
                  );

                  setForm((prev) => ({
                    ...prev,
                    vehicleId: selectedVehicle?.id || "",
                    vehicleNumber: selectedVehicle?.number || "",
                  }));
                }}
              >
                {vehicleList.map((vehicle) => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.number}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Select Driver"
                fullWidth
                size="small"
                value={form.driverId}
                onChange={(event) => {
                  const selectedDriver = driverList.find(
                    (driver) => driver.id === event.target.value
                  );

                  setForm((prev) => ({
                    ...prev,
                    driverId: selectedDriver?.id || "",
                    driverName: selectedDriver?.name || "",
                  }));
                }}
              >
                {driverList.map((driver) => (
                  <MenuItem key={driver.id} value={driver.id}>
                    {driver.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={form.date}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, date: event.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Vendor / Shop Name"
                fullWidth
                size="small"
                value={form.vendorName}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, vendorName: event.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Location"
                fullWidth
                size="small"
                value={form.location}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, location: event.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Person Logged By"
                fullWidth
                size="small"
                value={form.person}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, person: event.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Amount ₹"
                type="number"
                fullWidth
                size="small"
                value={form.amount}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, amount: event.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                multiline
                rows={2}
                fullWidth
                size="small"
                value={form.description}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, description: event.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12} textAlign="center">
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadIcon />}
                disabled={uploading}
              >
                {uploading ? "Uploading..." : "Upload Bill / Receipt Image"}
                <input type="file" hidden accept="image/*" onChange={handleImageChange} />
              </Button>

              {form.image && (
                <Typography variant="caption" display="block" sx={{ mt: 1, color: "green" }}>
                  Image Attached
                </Typography>
              )}
            </Grid>
          </Grid>

          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 3, py: 1.3, bgcolor: PRIMARY }}
            onClick={handleSubmit}
            disabled={loading || uploading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Save Record"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md">
        <IconButton
          onClick={() => setPreviewOpen(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            bgcolor: "rgba(0,0,0,0.5)",
            color: "white",
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        <img
          src={previewImage}
          alt="Attachment"
          style={{
            width: "100%",
            maxHeight: "80vh",
            objectFit: "contain",
          }}
        />
      </Dialog>
    </Box>
  );
}

export default Vehicalmgmt;